import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { walletAPI } from '../../utils/api';
import { Card } from '../../components/ui/card';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';

const PortfolioNew = () => {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const walletsRes = await walletAPI.getAll();

      setWallets(walletsRes.data || []);
    } catch (error) {
      console.error('Portfolio fetch error:', error);
      toast.error('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalValue = () => {
    return wallets.reduce(
      (sum, wallet) => sum + (wallet.balance || 0) * 86500,
      0
    );
  };

  const getPortfolioData = () => {
    if (wallets.length === 0) {
      return [
        { name: 'Bitcoin', value: 45, color: '#F7931A' },
        { name: 'Ethereum', value: 25, color: '#627EEA' },
        { name: 'Tether', value: 15, color: '#26A17B' },
        { name: 'Ripple', value: 10, color: '#23292F' },
        { name: 'Others', value: 5, color: '#6B7280' },
      ];
    }
    return wallets.map((wallet, idx) => ({
      name: wallet.asset_type,
      value: ((wallet.balance || 0) * 100) / wallets.length,
      color: ['#F7931A', '#627EEA', '#26A17B', '#23292F', '#6B7280'][idx % 5],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const totalBalance = calculateTotalValue();
  const portfolioData = getPortfolioData();

  return (
    <div
      className="space-y-6 p-4 md:p-6 lg:pl-10 lg:pr-6 lg:py-10 animate-fadeIn bg-[#F9F9F9]"
      data-testid="portfolio-page"
    >
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Portfolio
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm md:text-base">
          Track your crypto assets and performance
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Portfolio Distribution */}
        <Card className="lg:col-span-2 p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Portfolio Distribution
          </h2>
          <div
            className="flex items-center gap-6"
            style={{ minHeight: '280px' }}
          >
            {/* Chart on the left */}
            <div className="flex-1" style={{ minHeight: '280px' }}>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={portfolioData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {portfolioData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Legend/Tooltip on the right */}
            <div className="flex-shrink-0 w-48 space-y-2">
              {portfolioData.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {item.value.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Total Balance & Profit/Loss */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Total Balance
            </h2>
            <div className="flex items-baseline space-x-2">
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white">
                $
                {totalBalance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <span className="text-green-600 flex items-center text-base md:text-lg">
                <TrendingUp className="w-4 h-4 md:w-5 md:h-5 mr-1" />
                +12.5%
              </span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Since last month
            </p>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 bg-[#21bcfe]">
            <h2 className="text-xl font-bold text-gray-200 dark:text-white mb-2">
              Profit/Loss
            </h2>
            <div className="flex items-baseline space-x-2">
              <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-white">+$8,234.56</p>
            </div>
            <p className="text-gray-300 dark:text-gray-400 mt-2">
              Since last month
            </p>
          </Card>
        </div>
      </div>

      {/* Your Assets Table */}
      <Card className="p-6 overflow-hidden">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
          Your Assets
        </h2>
        <div className="responsive-table-wrapper">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                  Asset
                </th>
                <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                  Amount
                </th>
                <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                  Value
                </th>
                <th className="text-left text-sm font-medium text-gray-600 dark:text-gray-400 pb-3">
                  24h Change
                </th>
              </tr>
            </thead>
            <tbody>
              {wallets.length > 0 ? (
                wallets.map((wallet) => {
                  const value = (wallet.balance || 0) * 86500;
                  const change = Math.random() * 10 - 5;
                  return (
                    <tr
                      key={wallet.id}
                      className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                    >
                      <td className="py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <span className="text-blue-600 dark:text-blue-400 font-bold">
                              {wallet.asset_type?.charAt(0)}
                            </span>
                          </div>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {wallet.asset_type}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 text-gray-900 dark:text-white">
                        {(wallet.balance || 0).toFixed(4)} {wallet.asset_type}
                      </td>
                      <td className="py-4 text-gray-900 dark:text-white font-medium">
                        ${value.toLocaleString()}
                      </td>
                      <td className="py-4">
                        <span
                          className={`flex items-center ${change >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                        >
                          {change >= 0 ? (
                            <TrendingUp className="w-4 h-4 mr-1" />
                          ) : (
                            <TrendingDown className="w-4 h-4 mr-1" />
                          )}
                          {Math.abs(change).toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="text-center py-8 text-gray-500 dark:text-gray-400"
                  >
                    No assets found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default PortfolioNew;
